import { ICategory, SESSION_USER_CLAIMS, useItinInfo } from "@eventr/common";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { DEVTOOLS_ENABLED, GUEST_ORG_NAME } from "../../consts";
import { DataAPI } from "../../data/Data";
import { bs, useBoundStore } from "../../state/bound.store";
import { indexDbPersistStorage } from "../../state/indexdbStorage";
import { IItinerary } from "../../state/interfaces";
import { getTimezoneOfItinerary } from "../../utils";
import { create } from "../appState/resetState";
import { takeGuestPrefetch } from "./guestPrefetch";
import { logEvent } from "../firebase/firebase";

interface IGuestStore {
  guestData: {
    itinerary: IItinerary;
    map_entry: any;
    guest_token: string;
    org_config: {
      categories: ICategory[];
    };
  } | null;
  resolveGuestData: (data: any) => Promise<void>;
  guestCode: string;
  isGuestMode: boolean;
  cloneOnSignUp: boolean;
  setCloneOnSignUp: (cloneOnSignUp: boolean) => void;
  ingestGuestCode: (code: string) => Promise<boolean>;
}

/** True during a guest (public itinerary) session, including before guest-store has rehydrated. */
export function isGuestSession(): boolean {
  return useGuestStore.getState().isGuestMode || bs().selectedOrg?.name === GUEST_ORG_NAME;
}

/** Resolves in progress, keyed by guest code, so concurrent callers share one request. */
const inFlightResolves = new Map<string, Promise<void>>();

let rehydrationSettled = false;
const rehydrationWaiters = new Set<() => void>();

/** Resolves once the store's persist rehydration has settled, success or failure. */
function waitForRehydration(): Promise<void> {
  if (rehydrationSettled) return Promise.resolve();
  return new Promise((resolve) => {
    rehydrationWaiters.add(resolve);
  });
}

/** Whether the persisted guest state has read back, so `isGuestMode` can be trusted. */
export function isGuestStoreRehydrated(): boolean {
  return rehydrationSettled;
}

/** Resolves once the persisted guest state has read back, success or failure. */
export const whenGuestStoreRehydrated = waitForRehydration;

function settleRehydration(): void {
  rehydrationSettled = true;
  rehydrationWaiters.forEach((resolve) => resolve());
  rehydrationWaiters.clear();
}

/** Ends guest mode and drops its itinerary data. Tab availability and org gates read isGuestMode. */
export function clearGuestMode(): void {
  useGuestStore.setState({ guestData: null, guestCode: "", isGuestMode: false });
}

export const useGuestStore = create<IGuestStore>()(
  subscribeWithSelector(
    devtools(
      persist(
        (set, get) => ({
          guestData: null,
          guestCode: "",
          isGuestMode: false,
          cloneOnSignUp: false,
          setCloneOnSignUp: (cloneOnSignUp) => {
            set({ cloneOnSignUp }, false, "setCloneOnSignUp");
          },
          resolveGuestData: async (guestCode) => {
            const inFlight = inFlightResolves.get(guestCode);
            if (inFlight) return inFlight;

            const resolve = async () => {
              logEvent("guest_resolve", { code: guestCode });
              // On a guest cold boot the request is already in flight, started from
              // index.html before this bundle finished loading. Falls back to the normal
              // request whenever there is nothing usable prefetched for this code.
              const guestData = (await takeGuestPrefetch(guestCode)) ?? (await DataAPI.guest.getGuestData(guestCode));

              // The HTTP wrapper resolves on failure rather than throwing, so without this
              // check a failed call reaches `.data.map_entry` below and surfaces as a TypeError.
              if (guestData?.status !== "success" || !guestData.data) {
                throw new Error(guestData?.message ?? "The itinerary could not be loaded");
              }

              bs().setItineraryMap([guestData.data.map_entry]);
              localStorage.removeItem(SESSION_USER_CLAIMS);
              useBoundStore.setState({
                chatEnabled: false,
                teamPageEnabled: false,
              });
              // Must be set before setCurrentItin/setSelectedOrg below, which read isGuestMode.
              set(
                {
                  guestData: guestData.data,
                  guestCode: guestCode,
                  isGuestMode: true,
                },
                false,
                "resolveGuestData",
              );
              await bs().setCurrentItin(guestData.data.map_entry?.id ?? -1);
              bs().setSelectedOrg({ name: GUEST_ORG_NAME });
              if (guestData.data.org_config) bs().setOrgConfig(guestData.data.org_config);
              useItinInfo.getState().setTimezone(getTimezoneOfItinerary(guestData.data.itinerary));
              bs().pullEventData(guestData.data.map_entry?.id ?? -1);
            };

            const pending = resolve().finally(() => {
              inFlightResolves.delete(guestCode);
            });
            inFlightResolves.set(guestCode, pending);
            return pending;
          },
          ingestGuestCode: async (code) => {
            await waitForRehydration();
            const before = get();
            set({ guestCode: code, cloneOnSignUp: true }, false, "ingestGuestCode");
            if (before.guestCode === code && before.isGuestMode && before.guestData !== null) return true;
            try {
              await get().resolveGuestData(code);
              return true;
            } catch {
              return false;
            }
          },
        }),
        {
          name: "guest-store",
          storage: indexDbPersistStorage(),
          onRehydrateStorage: () => () => {
            settleRehydration();
          },
        },
      ),
      {
        name: "guest-store",
        enabled: DEVTOOLS_ENABLED,
      },
    ),
  ),
);
